/**
 * Shared job filtering + lexicon helpers for OpenPositionsPanel and agent tools.
 * Moss tenants inject ~114 roles with moss.com job-family labels; the voice
 * model speaks in plain English ("superintendent", "hourly solar", "QC tech").
 */
import { createLexicon, type Lexicon } from './primitives/lexicon';
import {
  jobMatchesLocationQuery,
  jobMatchesSearchQuery,
  normalizeSearchTokens,
  splitSearchAndLocation,
} from './locationSearch';
import {
  resolveRoleIntent,
  resolveRoleIntentFromArgs,
  splitSearchAndRoleIntent,
  type RoleTaxonomyIndex,
} from './roleTaxonomy';

/** Minimal job shape used by filters — matches OpenPositionsPanel.Job. */
export interface CatalogJob {
  id: number;
  title: string;
  department: string;
  track?: string;
  team?: string;
  location?: string;
  /** Normalized geographic index (states, cities, regions) for area filtering. */
  locationTags?: readonly string[];
  property?: string;
  type?: string;
  payRange?: string;
  skillMatches?: string[];
  description?: string;
  requirements?: string | null;
}

/** Synonym → moss.com job-family label. Targets must match scraped departments. */
export const MOSS_DEPARTMENT_SYNONYMS: Record<string, string> = {
  pm: 'CM Project Management',
  'project manager': 'CM Project Management',
  'project management': 'CM Project Management',
  'assistant project manager': 'CM Project Management',
  'project executive': 'CM Project Management',
  superintendent: 'CM Field Operations',
  'field superintendent': 'CM Field Operations',
  'senior superintendent': 'CM Field Operations',
  'field operations': 'CM Field Operations',
  'field ops': 'CM Field Operations',
  precon: 'CM Preconstruction',
  preconstruction: 'CM Preconstruction',
  estimating: 'Estimating',
  estimator: 'Estimating',
  engineering: 'Engineering',
  engineer: 'Engineering',
  'project engineer': 'Engineering',
  safety: 'Environmental, Health & Safety',
  ehs: 'Environmental, Health & Safety',
  'health and safety': 'Environmental, Health & Safety',
  solar: 'Solar Field Operations',
  laborer: 'Solar Field Operations',
  operator: 'Solar Field Operations',
  excavator: 'Solar Field Operations',
  cdl: 'Solar Field Operations',
  'truck driver': 'Solar Field Operations',
  'solar pm': 'Solar Project Management',
  'solar project': 'Solar Project Management',
  qc: 'Solar Quality Control/ Warranty',
  'quality control': 'Solar Quality Control/ Warranty',
  warranty: 'Solar Quality Control/ Warranty',
  finance: 'Finance & Accounting',
  accounting: 'Finance & Accounting',
  tax: 'Finance & Accounting',
  hr: 'Human Resources',
  recruiting: 'Human Resources',
  'early careers': 'Human Resources',
  marketing: 'Marketing & Business Development',
  communications: 'Marketing & Business Development',
  it: 'Digital & Information Technology',
  technology: 'Digital & Information Technology',
  logistics: 'Procurement & Logistics',
  procurement: 'Procurement & Logistics',
  equipment: 'Asset Management',
  scheduling: 'Planning & Scheduling',
  scheduler: 'Planning & Scheduling',
  commissioning: 'Commissioning',
  executive: 'Executive',
  corporate: 'Corporate Administration',
  risk: 'Risk Management',
  'early stage': 'Early Stage Project Management',
};

export const MOSS_TRACK_SYNONYMS: Record<string, string> = {
  professional: 'Professionals (Salaried)',
  professionals: 'Professionals (Salaried)',
  salaried: 'Professionals (Salaried)',
  exempt: 'Professionals (Salaried)',
  cm: 'Professionals (Salaried)',
  'construction management': 'Professionals (Salaried)',
  office: 'Professionals (Salaried)',
  solar: 'Solar Hourly',
  hourly: 'Solar Hourly',
  field: 'Solar Hourly',
  'solar hourly': 'Solar Hourly',
  travel: 'Solar Hourly',
  intern: 'Professionals (Salaried)',
  internship: 'Professionals (Salaried)',
};

export function buildDepartmentLexicon(departments: readonly string[]): Lexicon {
  const canonical = [...new Set(departments.filter(Boolean))].sort();
  return createLexicon({
    canonical,
    synonyms: MOSS_DEPARTMENT_SYNONYMS,
  });
}

export function buildTrackLexicon(tracks: readonly string[]): Lexicon {
  const canonical = [...new Set(tracks.filter(Boolean))].sort();
  return createLexicon({
    canonical,
    synonyms: MOSS_TRACK_SYNONYMS,
  });
}

/** Rich search corpus — every token the agent or user might say. */
export function getJobSearchText(job: CatalogJob): string {
  return [
    job.title,
    job.department,
    job.track ?? '',
    job.team ?? '',
    job.location ?? '',
    ...(job.locationTags ?? []),
    job.property ?? '',
    job.type ?? '',
    job.payRange ?? '',
    job.description ?? '',
    job.requirements ?? '',
    ...(job.skillMatches ?? []),
  ]
    .join(' ')
    .toLowerCase();
}

function jobMatchesSearchTokens(job: CatalogJob, query: string): boolean {
  return jobMatchesSearchQuery(job, query);
}

export interface JobFilterArgs {
  /** Conversational role type spanning multiple departments (from roleTaxonomy). */
  roleIntent?: string;
  department?: string;
  track?: string;
  /** Geographic area — state, city, or region (Texas, DFW, South Florida, Colorado). */
  location?: string;
  search?: string;
}

/** Promote role phrases and geographic terms from search; resolve roleIntent. */
export function normalizeJobFilterArgs(
  args: JobFilterArgs,
  roleIndex?: RoleTaxonomyIndex,
): JobFilterArgs {
  let roleIntent = args.roleIntent?.trim() || undefined;
  let department = args.department?.trim() || undefined;
  let track = args.track?.trim() || undefined;
  let location = args.location?.trim() || undefined;
  let search = args.search?.trim() || undefined;

  if (roleIndex) {
    if (roleIntent) {
      roleIntent = resolveRoleIntent(roleIntent, roleIndex) ?? roleIntent;
    } else {
      const resolved = resolveRoleIntentFromArgs(
        { department, search },
        roleIndex,
      );
      roleIntent = resolved.roleIntent;
      department = resolved.department;
      search = resolved.search;
    }
  }

  if (location || !search) {
    return { roleIntent, department, track, location, search };
  }

  const roleSplit = roleIndex ? splitSearchAndRoleIntent(search, roleIndex) : {};
  if (roleSplit.roleIntent && !roleIntent) {
    roleIntent = roleSplit.roleIntent;
    search = roleSplit.search;
  }

  const locSplit = splitSearchAndLocation(search ?? '');
  return {
    roleIntent,
    department,
    track,
    location: locSplit.location ?? location,
    search: locSplit.search,
  };
}

function tokenizeSearch(q: string): string[] {
  return normalizeSearchTokens(q);
}

/** Filter jobs — multi-token search (all tokens must match somewhere in corpus). */
export function filterJobs(
  jobs: readonly CatalogJob[],
  args: JobFilterArgs,
  departmentLexicon?: Lexicon,
  trackLexicon?: Lexicon,
  roleIndex?: RoleTaxonomyIndex,
): CatalogJob[] {
  let normalizedArgs = normalizeJobFilterArgs(args, roleIndex);

  if (
    roleIndex &&
    normalizedArgs.roleIntent &&
    !normalizedArgs.track?.trim()
  ) {
    const entry = roleIndex.byId.get(normalizedArgs.roleIntent);
    if (entry?.defaultTrack) {
      normalizedArgs = { ...normalizedArgs, track: entry.defaultTrack };
    }
  }

  let result = [...jobs];

  if (normalizedArgs.track?.trim()) {
    const trackLex = trackLexicon ?? buildTrackLexicon(jobs.map((j) => j.track ?? '').filter(Boolean));
    const normalized = trackLex.normalize(normalizedArgs.track) ?? normalizedArgs.track.trim();
    result = result.filter((j) => (j.track ?? '') === normalized);
  }

  if (normalizedArgs.department?.trim()) {
    const deptLex =
      departmentLexicon ??
      buildDepartmentLexicon(jobs.map((j) => j.department));
    const normalized = deptLex.normalize(normalizedArgs.department);
    if (normalized) {
      result = result.filter((j) => j.department === normalized);
    } else {
      result = result.filter((j) => jobMatchesSearchTokens(j, normalizedArgs.department!));
    }
  } else if (normalizedArgs.roleIntent?.trim() && roleIndex) {
    const entry = roleIndex.byId.get(normalizedArgs.roleIntent);
    if (entry) {
      const deptSet = new Set(entry.departments);
      result = result.filter((j) => deptSet.has(j.department));
    }
  }

  if (normalizedArgs.location?.trim()) {
    result = result.filter((j) => jobMatchesLocationQuery(j, normalizedArgs.location!));
  }

  if (normalizedArgs.search?.trim()) {
    result = result.filter((j) => jobMatchesSearchTokens(j, normalizedArgs.search!));
  }

  return result;
}

/** Score title match — higher is better. */
function titleMatchScore(jobTitle: string, query: string): number {
  const t = jobTitle.toLowerCase();
  const q = query.toLowerCase().trim();
  if (!q) return 0;
  if (t === q) return 100;
  if (t.includes(q)) return 80 + Math.min(15, q.length);
  const qTokens = tokenizeSearch(q);
  const matched = qTokens.filter((tok) => t.includes(tok)).length;
  if (matched === qTokens.length && qTokens.length > 0) return 60 + matched * 5;
  return matched * 10;
}

/** Best job match by title; returns top match or undefined. */
export function findJobByTitle(
  jobs: readonly CatalogJob[],
  titleQuery: string,
): CatalogJob | undefined {
  const scored = jobs
    .map((j) => ({ job: j, score: titleMatchScore(j.title, titleQuery) }))
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score);
  return scored[0]?.job;
}

/** Top N matches for disambiguation messages. */
export function findJobTitleMatches(
  jobs: readonly CatalogJob[],
  titleQuery: string,
  limit = 5,
): CatalogJob[] {
  return jobs
    .map((j) => ({ job: j, score: titleMatchScore(j.title, titleQuery) }))
    .filter((x) => x.score >= 30)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map((x) => x.job);
}

export function formatJobsToolResult(
  filtered: readonly CatalogJob[],
  args: JobFilterArgs,
): string {
  const parts: string[] = [];
  if (args.roleIntent) parts.push(`roleIntent=${args.roleIntent}`);
  if (args.department) parts.push(`department=${args.department}`);
  if (args.track) parts.push(`track=${args.track}`);
  if (args.location) parts.push(`location="${args.location}"`);
  if (args.search) parts.push(`search="${args.search}"`);
  const filterDesc = parts.length > 0 ? parts.join(', ') : 'no filter';

  if (filtered.length === 0) {
    return `Opened Open Positions panel (${filterDesc}). No matching roles — try a broader search term or a different department/track.`;
  }

  const preview = filtered
    .slice(0, 8)
    .map((j) => `#${j.id} ${j.title} (${j.department}${j.track ? ` · ${j.track}` : ''})`)
    .join('; ');

  return `Opened Open Positions panel (${filterDesc}). Showing ${filtered.length} role${filtered.length === 1 ? '' : 's'}: ${preview}${filtered.length > 8 ? '…' : ''}.`;
}
